/* Hero slideshow web component.
   Scroll-snap slider with dot navigation, autoplay with a donut countdown ring
   (drawn in CSS, keyed off the is-active dot), pause on hover/focus/hidden tab,
   and theme editor block-select support. Self-contained; no Dawn JS dependencies. */
(function () {
  if (customElements.get('custom-hero-slideshow')) return;

  class CustomHeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.custom-hero__track');
      this.slides = Array.from(this.querySelectorAll('.custom-hero__slide'));
      this.dots = Array.from(this.querySelectorAll('.custom-hero__dot'));
      if (!this.track || this.slides.length === 0) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.speed = (parseInt(this.dataset.speed, 10) || 7) * 1000;
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1 && !this.reducedMotion;
      this.current = 0;
      this.remaining = this.speed;
      this.pauseReasons = new Set();
      this.userScrolling = false;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);

      this.track.addEventListener('scroll', this.onScroll);
      window.addEventListener('resize', this.onResize);
      document.addEventListener('visibilitychange', this.onVisibility);

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index));
      });

      // Pause while the pointer rests on the hero (real hover devices only)
      if (window.matchMedia('(hover: hover)').matches) {
        this.addEventListener('mouseenter', () => this.pause('hover'));
        this.addEventListener('mouseleave', () => this.resume('hover'));
      }
      this.addEventListener('focusin', () => this.pause('focus'));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.resume('focus');
      });
      // A touch drag pauses the timer; the scroll settle handler restarts it.
      // A plain tap never scrolls, so pointerup must also schedule a resume.
      this.track.addEventListener('pointerdown', () => {
        this.userScrolling = true;
        this.pause('drag');
      });
      this.track.addEventListener('pointerup', () => this.endDragSoon());
      this.track.addEventListener('pointercancel', () => this.endDragSoon());

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      if (this.autoplay) {
        this.classList.add('custom-hero--autoplay');
        this.startTimer(this.speed);
      }
      // The JS deliberately NEVER touches the videos — no play(), no
      // pause(). Every slide's video autoplays muted via attributes alone
      // (the proven old-video-banner model). Strict WKWebView browsers
      // (Vivaldi iOS) tear a video down on any script play(), gesture or
      // not, leaving a permanent blank; attribute-only playback is the only
      // behavior that works everywhere.
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.scrollDebounce);
      clearTimeout(this.resizeDebounce);
      clearTimeout(this.dragTimeout);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    /* --- autoplay timer --- */

    startTimer(ms) {
      if (!this.autoplay) return;
      clearTimeout(this.timer);
      this.endTime = Date.now() + ms;
      this.timer = setTimeout(() => this.goTo((this.current + 1) % this.slides.length, true), ms);
    }

    pause(reason) {
      this.pauseReasons.add(reason);
      if (!this.autoplay) return;
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
        this.remaining = Math.max(this.endTime - Date.now(), 500);
      }
      this.classList.add('is-paused');
    }

    resume(reason) {
      this.pauseReasons.delete(reason);
      if (!this.autoplay || this.pauseReasons.size > 0) return;
      this.classList.remove('is-paused');
      this.startTimer(this.remaining);
    }

    endDragSoon() {
      clearTimeout(this.dragTimeout);
      this.dragTimeout = setTimeout(() => {
        if (this.userScrolling) {
          this.userScrolling = false;
          this.resume('drag');
        }
      }, 250);
    }

    /* --- navigation --- */

    goTo(index, fromTimer) {
      const behavior = this.reducedMotion ? 'auto' : 'smooth';
      this.track.scrollTo({ left: this.slides[index].offsetLeft, behavior: behavior });
      this.setActive(index, !fromTimer);
    }

    setActive(index, restartRing) {
      this.current = index;
      // is-active on the slide drives the Ken Burns zoom (CSS); re-activating
      // the same slide restarts it the same way the dot ring restarts
      this.slides.forEach((slide, i) => {
        const active = i === index;
        if (restartRing && active && slide.classList.contains('is-active')) {
          slide.classList.remove('is-active');
          void slide.offsetWidth;
        }
        slide.classList.toggle('is-active', active);
      });
      this.dots.forEach((dot, i) => {
        const active = i === index;
        if (restartRing && active && dot.classList.contains('is-active')) {
          // Same dot re-activated: force the countdown animation to restart
          dot.classList.remove('is-active');
          void dot.offsetWidth;
        }
        dot.classList.toggle('is-active', active);
        if (active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
      this.remaining = this.speed;
      if (this.pauseReasons.size === 0) this.startTimer(this.speed);
    }

    /* --- event handlers --- */

    onScroll() {
      clearTimeout(this.scrollDebounce);
      this.scrollDebounce = setTimeout(() => {
        const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
        const clamped = Math.min(Math.max(index, 0), this.slides.length - 1);
        if (clamped !== this.current) {
          this.setActive(clamped, true);
        } else if (this.userScrolling) {
          // Swipe that settled back on the same slide: restart the countdown
          this.setActive(clamped, true);
        }
        if (this.userScrolling) {
          this.userScrolling = false;
          this.resume('drag');
        }
      }, 120);
    }

    onResize() {
      clearTimeout(this.resizeDebounce);
      this.resizeDebounce = setTimeout(() => {
        this.track.scrollTo({ left: this.slides[this.current].offsetLeft, behavior: 'auto' });
      }, 150);
    }

    onVisibility() {
      if (document.hidden) {
        this.pause('hidden');
      } else {
        this.resume('hidden');
      }
    }

    onBlockSelect(event) {
      const slide = event.target.closest && event.target.closest('.custom-hero__slide');
      if (!slide || !this.contains(slide)) return;
      this.pause('editor');
      const index = this.slides.indexOf(slide);
      if (index !== -1) {
        this.track.scrollTo({ left: this.slides[index].offsetLeft, behavior: 'auto' });
        this.setActive(index, true);
      }
    }

    onBlockDeselect(event) {
      const slide = event.target.closest && event.target.closest('.custom-hero__slide');
      if (!slide || !this.contains(slide)) return;
      this.resume('editor');
    }
  }

  customElements.define('custom-hero-slideshow', CustomHeroSlideshow);
})();
